/**
 * src/lib/lookups/LookupContext.jsx — single source of truth for ACN lookup tables
 *
 * Phase C1 of the 2026-04-06 schema reconciliation. Replaces hardcoded Spanish
 * stage arrays in CashFlowTimeline.jsx, DealCard.jsx, and crm.js with live data
 * from Supabase. Reads bilingual labels (label_en / label_es) and exposes them
 * via a hook that's locale-aware (reads from I18nContext).
 *
 * Per acn-project skill v1.1: every finite-value field in the UI must read from
 * the corresponding lookup table, not from hardcoded arrays. This file makes
 * that possible without rewriting every consumer at once.
 *
 * Tables loaded at boot:
 *   - stages (CRM scope: joined to stage_flow_steps + stage_flows where flow_type='crm')
 *   - stages (project scope: same but flow_type='project')
 *   - task_statuses (flat lookup)
 *   - project_types (flat lookup)
 *   - product_categories (flat lookup — Phase C)
 *   - work_types (flat lookup — Phase D)
 *
 * Hooks exported:
 *   useLookups()          — full provider value
 *   useCrmStages()        — array of CRM stages, sort_order ascending
 *   useProjectStages()    — array of project stages
 *   useTaskStatuses()     — array of task statuses
 *   useProjectTypes()     — array of project types
 *   useProductCategories()— array of product categories (Phase C)
 *   useWorkTypes()        — array of work types (Phase D)
 *   useProjectTypeStyle() — DB-driven type color resolver: getTypeStyle(type) → {bg, text} (Phase B)
 *   useStageColor()       — DB-driven stage color resolver: getStageColor(stage) → hex/var
 *   useStageLabel(s)      — locale-aware label resolver: useStageLabel(stage) → string
 */

import { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import supabase from '../supabase.js';
import { useT } from '../i18n/I18nContext.jsx';

const LookupContext = createContext(null);

const EMPTY = { crmStages: [], projectStages: [], taskStatuses: [], projectTypes: [], productCategories: [], workTypes: [], loading: true, error: null };

async function loadStagesByFlowType(flowType) {
  // Stages are scoped via stage_flow_steps + stage_flows.flow_type.
  // The stages table has NO flow_type column — see skill v1.1 note.
  const { data, error } = await supabase
    .from('stage_flow_steps')
    .select('step_order, stage:stages!inner(id, slug, label_en, label_es, color, is_terminal), flow:stage_flows!inner(id, flow_type)')
    .eq('flow.flow_type', flowType)
    .order('step_order', { ascending: true });

  if (error) {
    console.warn(`[lookups] failed to load ${flowType} stages:`, error.message);
    return [];
  }

  // Flatten + dedupe (a stage may belong to multiple flows; we want each stage once,
  // ordered by its first step_order in the requested flow_type).
  const seen = new Set();
  const out = [];
  for (const row of data || []) {
    if (!row.stage || seen.has(row.stage.id)) continue;
    seen.add(row.stage.id);
    out.push({ ...row.stage, sort_order: row.step_order });
  }
  return out;
}

async function loadFlat(table) {
  const { data, error } = await supabase
    .from(table)
    .select('id, slug, label_en, label_es, color, is_active, sort_order')
    .order('sort_order', { ascending: true });
  if (error) {
    console.warn(`[lookups] failed to load ${table}:`, error.message);
    return [];
  }
  return (data || []).filter(r => r.is_active !== false);
}

async function loadProjectTypes() {
  // project_types may not have a slug column in older revisions. Be permissive.
  const { data, error } = await supabase
    .from('project_types')
    .select('*')
    .order('sort_order', { ascending: true });
  if (error) {
    console.warn('[lookups] failed to load project_types:', error.message);
    return [];
  }
  return data || [];
}

async function loadProductCategories() {
  const { data, error } = await supabase
    .from('product_categories')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });
  if (error) {
    console.warn('[lookups] failed to load product_categories:', error.message);
    return [];
  }
  return data || [];
}

async function loadWorkTypes() {
  const { data, error } = await supabase
    .from('work_types')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });
  if (error) {
    console.warn('[lookups] failed to load work_types:', error.message);
    return [];
  }
  return data || [];
}

export function LookupProvider({ children }) {
  const [state, setState] = useState(EMPTY);

  useEffect(() => {
    let cancelled = false;

    // 2026-04-26 (B5a): LookupProvider mounts BEFORE AuthProvider in the
    // tree (see src/main.jsx). The first fetch fires without a JWT, RLS
    // returns zero rows, and the empty cache poisons every dropdown for
    // the lifetime of the page (Category form fell back to a hardcoded
    // list that included services AC Nautitech doesn't offer).
    // Fix: refetch when supabase reports SIGNED_IN / TOKEN_REFRESHED so a
    // late-arriving session re-hydrates the lookup tables.
    const loadAll = async () => {
      try {
        const [crmStages, projectStages, taskStatuses, projectTypes, productCategories, workTypes] = await Promise.all([
          loadStagesByFlowType('crm'),
          loadStagesByFlowType('project'),
          loadFlat('task_statuses'),
          loadProjectTypes(),
          loadProductCategories(),
          loadWorkTypes(),
        ]);
        if (cancelled) return;
        setState({ crmStages, projectStages, taskStatuses, projectTypes, productCategories, workTypes, loading: false, error: null });
      } catch (err) {
        console.error('[lookups] boot failed:', err);
        if (!cancelled) setState(s => ({ ...s, loading: false, error: err.message }));
      }
    };

    loadAll();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
        loadAll();
      }
    });

    return () => {
      cancelled = true;
      subscription?.unsubscribe();
    };
  }, []);

  return <LookupContext.Provider value={state}>{children}</LookupContext.Provider>;
}

export function useLookups() {
  const ctx = useContext(LookupContext);
  return ctx || EMPTY;
}

export function useCrmStages() {
  return useLookups().crmStages;
}

export function useProjectStages() {
  return useLookups().projectStages;
}

export function useTaskStatuses() {
  return useLookups().taskStatuses;
}

export function useProjectTypes() {
  return useLookups().projectTypes;
}

export function useProductCategories() {
  return useLookups().productCategories;
}

export function useWorkTypes() {
  return useLookups().workTypes;
}

/**
 * useProjectTypeStyle — DB-driven project type color resolver.
 * Returns a callback `getTypeStyle(typeInput)` that resolves a project type
 * (by slug, label_en, or label_es) to `{ bg, text }` from the DB row.
 * Fallback: neutral gray.
 */
export function useProjectTypeStyle() {
  const projectTypes = useProjectTypes();

  return useCallback((typeInput) => {
    const fallback = { bg: 'var(--bdrg)', text: 'var(--tm)' };
    if (!typeInput) return fallback;

    const s = String(typeInput);
    const match = projectTypes.find(pt =>
      pt.slug === s || pt.label_en === s || pt.label_es === s || pt.type_name === s
    );
    if (match?.color_bg && match?.color_text) {
      return { bg: match.color_bg, text: match.color_text };
    }
    // Case-insensitive retry
    const lower = s.toLowerCase();
    const byLower = projectTypes.find(pt =>
      (pt.slug && pt.slug.toLowerCase() === lower) ||
      (pt.label_en && pt.label_en.toLowerCase() === lower) ||
      (pt.label_es && pt.label_es.toLowerCase() === lower) ||
      (pt.type_name && pt.type_name.toLowerCase() === lower)
    );
    if (byLower?.color_bg && byLower?.color_text) {
      return { bg: byLower.color_bg, text: byLower.color_text };
    }
    return fallback;
  }, [projectTypes]);
}

/**
 * useStageColor — DB-driven stage color resolver.
 * Combines CRM + project stages and returns a callback `getStageColor(stageInput)`
 * that resolves a stage (string or object with `.color`) to its DB color.
 *
 * Resolution order:
 *   1. If stageInput is an object with `.color`, return it directly
 *   2. Try slug match against all stages
 *   3. Try label match (label_en, label_es, stage_name)
 *   4. Strip numeric prefix ("2. Pedir materiales" → "Pedir materiales") and retry
 *   5. Fallback: '#6B7280'
 */
export function useStageColor() {
  const { crmStages, projectStages } = useLookups();
  const allStages = useMemo(
    () => [...crmStages, ...projectStages],
    [crmStages, projectStages]
  );

  return useCallback((stageInput) => {
    if (!stageInput) return 'var(--tm)';

    // 1. Object with .color
    if (typeof stageInput === 'object' && stageInput.color) return stageInput.color;

    const s = String(stageInput);

    // 2. Slug match
    const bySlug = allStages.find(st => st.slug === s);
    if (bySlug?.color) return bySlug.color;

    // 3. Label match (label_en, label_es, or stage_name)
    const byLabel = allStages.find(st =>
      st.label_en === s || st.label_es === s || st.stage_name === s
    );
    if (byLabel?.color) return byLabel.color;

    // 4. Strip numeric prefix and retry
    const stripped = s.replace(/^\d+\.\s*/, '');
    if (stripped !== s) {
      const byStrippedSlug = allStages.find(st => st.slug === stripped);
      if (byStrippedSlug?.color) return byStrippedSlug.color;
      const byStrippedLabel = allStages.find(st =>
        st.label_en === stripped || st.label_es === stripped || st.stage_name === stripped
      );
      if (byStrippedLabel?.color) return byStrippedLabel.color;
    }

    // 4b. Case-insensitive retry
    const lower = stripped.toLowerCase();
    const byLower = allStages.find(st =>
      (st.slug && st.slug.toLowerCase() === lower) ||
      (st.label_en && st.label_en.toLowerCase() === lower) ||
      (st.label_es && st.label_es.toLowerCase() === lower)
    );
    if (byLower?.color) return byLower.color;

    // 5. Fallback
    return 'var(--tm)';
  }, [allStages]);
}

/**
 * useLabel — locale-aware label resolver for any lookup row.
 * Usage: const label = useLabel(stageRow)
 *        const label = useLabel({ label_en: 'Lead', label_es: 'Lead' })
 */
export function useLabel(row) {
  const { locale } = useT();
  if (!row) return '';
  return row[`label_${locale}`] || row.label_en || row.label_es || row.slug || '';
}

/**
 * useLabelFn — returns a stable function (label_en/label_es picker by current locale).
 * Use when you need to label many rows in the same render.
 */
export function useLabelFn() {
  const { locale } = useT();
  return useMemo(() => (row) => {
    if (!row) return '';
    return row[`label_${locale}`] || row.label_en || row.label_es || row.slug || '';
  }, [locale]);
}

/**
 * findCrmStageBy — small util to find a CRM stage by slug or by either label.
 * Useful when migrating legacy code that has a Spanish (or English) status string
 * and needs the stage row.
 */
export function findCrmStageBy(stages, value) {
  if (!value || !Array.isArray(stages)) return null;
  return stages.find(s =>
    s.slug === value || s.label_es === value || s.label_en === value
  ) || null;
}
